import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, RefreshCw, Plus, Link2, FileText, IdCard, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const BriefCard = ({ brief, requestedKeys, onRequestPassport }) => (
    <div data-testid={`brief-card-${brief.id}`} className="card-soft p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-lg text-foreground">
                        {brief.job_profile?.title || "Analyzing role…"}
                    </h3>
                    {brief.status === "pending_review" && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                            <Loader2 size={11} className="animate-spin" /> Awaiting Simonara review
                        </span>
                    )}
                    {brief.status === "processing" && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground dark:text-emerald-300">
                            <Loader2 size={11} className="animate-spin" /> AI analyzing
                        </span>
                    )}
                    {brief.status === "ready" && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                            <Sparkles size={11} /> Brief ready
                        </span>
                    )}
                    {brief.status === "failed" && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1 text-xs font-semibold text-destructive">
                            <AlertTriangle size={11} /> Failed
                        </span>
                    )}
                </div>
                <div className="mt-1 text-sm text-muted-foreground flex flex-wrap gap-x-3">
                    {brief.job_profile?.company && <span>{brief.job_profile.company}</span>}
                    {brief.job_profile?.location && <span>· {brief.job_profile.location}</span>}
                    {brief.job_profile?.employment_type && <span>· {brief.job_profile.employment_type}</span>}
                    {brief.job_profile?.salary && <span>· {brief.job_profile.salary}</span>}
                </div>
            </div>
            <div className="text-xs text-muted-foreground">
                {brief.created_at ? new Date(brief.created_at).toLocaleString() : ""}
            </div>
        </div>

        {brief.status === "pending_review" && (
            <div className="mt-4 rounded-xl bg-amber-500/5 border border-amber-500/20 p-4 text-sm text-muted-foreground flex items-start gap-3">
                <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                    <div className="font-semibold text-foreground">Your brief is queued for review.</div>
                    <div className="mt-0.5">A Simonara Talent Ops specialist is verifying the role before AI matching. Approved briefs typically start matching within 1 business hour.</div>
                    {brief.admin_note && (
                        <div className="mt-2 rounded-lg bg-muted p-2 text-xs">
                            <span className="font-semibold">Note from Simonara:</span> {brief.admin_note}
                        </div>
                    )}
                </div>
            </div>
        )}

        {brief.status === "declined" && (
            <div className="mt-4 rounded-xl bg-destructive/5 border border-destructive/20 p-4 text-sm text-muted-foreground">
                <div className="font-semibold text-destructive">Brief declined by Simonara.</div>
                {brief.admin_note && <div className="mt-1">{brief.admin_note}</div>}
            </div>
        )}

        {brief.status === "processing" && (
            <div className="mt-4 rounded-xl bg-muted p-4 text-sm text-muted-foreground flex items-center gap-3">
                <Loader2 size={16} className="animate-spin text-emerald-500" />
                Simonara AI is analyzing the role and scanning verified Talent Passports. This usually takes under a minute.
            </div>
        )}

        {brief.status === "failed" && (
            <div className="mt-4 rounded-xl bg-destructive/5 border border-destructive/20 p-4 text-sm text-muted-foreground">
                AI analysis failed for this submission. Please try submitting the job again.
            </div>
        )}

        {brief.status === "ready" && (
            <>
                {brief.job_profile?.summary && (
                    <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{brief.job_profile.summary}</p>
                )}
                {(brief.job_profile?.skills || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {brief.job_profile.skills.slice(0, 8).map((s) => (
                            <span key={s} className="rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground">{s}</span>
                        ))}
                    </div>
                )}
                <div className="mt-5">
                    <div className="flex items-center justify-between">
                        <h4 className="font-heading font-semibold text-foreground flex items-center gap-2">
                            <Sparkles size={15} className="text-emerald-500" /> AI Hiring Brief™
                        </h4>
                        <span className="mono-score text-[11px] uppercase tracking-wider text-muted-foreground">
                            Confidence {Math.round((brief.confidence || 0) * 100)}%
                        </span>
                    </div>
                    {brief.overall_summary && (
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{brief.overall_summary}</p>
                    )}
                    <div className="mt-4 space-y-3">
                        {(brief.top_matches || []).length === 0 && (
                            <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground">
                                No candidates in the Talent Passport database matched this role yet. A Simonara recruiting specialist can step in — <Link to="/contact" className="text-brand-green dark:text-emerald-400 font-semibold hover:underline">contact us</Link>.
                            </div>
                        )}
                        {(brief.top_matches || []).map((m) => (
                            <div key={`${m.candidate_id}-${m.match_score}`} className="rounded-xl border border-border bg-card p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="font-heading font-semibold text-foreground text-sm">{m.name}</div>
                                        <div className="text-xs text-muted-foreground">{m.headline}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="mono-score text-2xl font-bold text-emerald-600 dark:text-emerald-400">{m.match_score}%</div>
                                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">AI Match</div>
                                    </div>
                                </div>
                                <ul className="mt-3 space-y-1.5">
                                    {(m.fit_reasons || []).map((r, i) => (
                                        <li key={i} className="flex items-start gap-2 text-xs text-foreground">
                                            <CheckCircle2 size={13} className="text-emerald-500 shrink-0 mt-0.5" /> {r}
                                        </li>
                                    ))}
                                </ul>
                                {(m.gaps || []).length > 0 && (
                                    <div className="mt-2.5 flex items-start gap-2 text-xs text-muted-foreground">
                                        <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                                        <span>Potential gap: {m.gaps.join("; ")}</span>
                                    </div>
                                )}
                                <div className="mt-3 pt-3 border-t border-border">
                                    {requestedKeys.has(`${brief.id}:${m.candidate_id}`) ? (
                                        <span data-testid={`passport-requested-${m.candidate_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                                            <Check size={12} /> Passport requested — Simonara will follow up
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => onRequestPassport(brief.id, m)}
                                            data-testid={`request-passport-${m.candidate_id}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-accent px-3.5 py-1.5 text-xs font-semibold text-accent-foreground dark:text-emerald-300 hover:bg-emerald-500 hover:text-white transition-colors"
                                        >
                                            <IdCard size={13} /> Request full Talent Passport
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </>
        )}
    </div>
);

export default function EmployerDashboard() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [briefs, setBriefs] = useState(null);
    const [requestedKeys, setRequestedKeys] = useState(new Set());
    const pollRef = useRef(null);

    const fetchRequests = useCallback(async () => {
        try {
            const { data } = await api.get("/employer/passport-requests");
            setRequestedKeys(new Set(data.map((r) => `${r.brief_id}:${r.candidate_id}`)));
        } catch { /* non-blocking */ }
    }, []);

    const requestPassport = async (briefId, match) => {
        try {
            await api.post("/employer/passport-requests", {
                brief_id: briefId,
                candidate_id: match.candidate_id,
                candidate_name: match.name,
            });
            setRequestedKeys((prev) => new Set(prev).add(`${briefId}:${match.candidate_id}`));
            toast.success(`Talent Passport requested for ${match.name}. Simonara will follow up shortly.`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const fetchBriefs = useCallback(async () => {
        try {
            const { data } = await api.get("/employer/briefs");
            setBriefs(data);
            return data;
        } catch (err) {
            if (err?.response?.status === 401 || err?.response?.status === 403) navigate("/login");
            else toast.error(formatApiError(err));
            return [];
        }
    }, [navigate]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/login"); return; }
        if (user.role !== "employer") { navigate("/professional/dashboard"); return; }
        fetchBriefs();
        fetchRequests();
    }, [authLoading, user, fetchBriefs, fetchRequests, navigate]);

    useEffect(() => {
        const hasProcessing = (briefs || []).some((b) => b.status === "processing");
        if (hasProcessing && !pollRef.current) {
            pollRef.current = setInterval(async () => {
                const data = await fetchBriefs();
                if (!data.some((b) => b.status === "processing")) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                }
            }, 5000);
        }
        return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    }, [briefs, fetchBriefs]);

    return (
        <div className="bg-background min-h-[80vh]" data-testid="employer-dashboard">
            <div className="container-page py-12">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <p className="overline">Simonara Hire™</p>
                        <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""} 👋
                        </h1>
                        {user?.company_name && <p className="text-sm text-muted-foreground mt-1">{user.company_name}</p>}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={fetchBriefs} data-testid="employer-refresh" className="btn-outline text-sm py-2.5 px-5">
                            <RefreshCw size={15} className="mr-2" /> Refresh
                        </button>
                        <Link to="/employers#request-talent" data-testid="employer-new-job" className="btn-green text-sm py-2.5 px-5">
                            <Plus size={15} className="mr-2" /> Submit a job
                        </Link>
                    </div>
                </div>

                <div className="mt-10">
                    {briefs === null ? (
                        <div className="flex items-center gap-3 text-muted-foreground"><Loader2 size={18} className="animate-spin" /> Loading your hiring briefs…</div>
                    ) : briefs.length === 0 ? (
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-soft p-10 text-center max-w-xl mx-auto">
                            <div className="mx-auto w-12 h-12 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                <Sparkles size={22} />
                            </div>
                            <h2 className="mt-4 font-heading font-semibold text-xl text-foreground">Submit your first job</h2>
                            <p className="mt-2 text-sm text-muted-foreground">
                                Paste a job posting URL, upload a description, or paste the text — Simonara AI will deliver
                                an AI Hiring Brief™ with your best Talent Passport matches in minutes.
                            </p>
                            <div className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1.5"><Link2 size={12} /> URL</span>
                                <span className="inline-flex items-center gap-1.5"><FileText size={12} /> PDF / Word</span>
                                <span>Paste text</span>
                            </div>
                            <Link to="/employers#request-talent" data-testid="employer-empty-cta" className="btn-green mt-6 inline-flex">Find Talent</Link>
                        </motion.div>
                    ) : (
                        <div className="space-y-5">
                            {briefs.map((b) => <BriefCard key={b.id} brief={b} requestedKeys={requestedKeys} onRequestPassport={requestPassport} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
